import './style.scss'

import axios from 'axios'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { RegistrationModalWrapper } from '~/components/modals'
import { setNotification } from '~/components/notification'
import { PageMeta } from '~/components/PageMeta'
import { PageKey } from '~/constants/pageMeta'
import { GOOGLE_EVENTS, sendGoogleGoal } from '~/libs/analytic/ga'
import { trackPublicCatalogLink } from '~/libs/analytic/public-catalog'
import * as PublicEmptyLegsApi from '~/services/api/public-empty-legs'

import { SiteFooter } from '../../components/site-footer'
import { SiteHeader } from '../../components/site-header'
import { openFormRegistration } from '../main/redux/rtk/mainSlice'
import { hasPublicEmptyLegSearchCriteria } from './application/public-empty-leg-filters'
import {
    PublicEmptyLegFilterUpdate,
    PublicEmptyLegMarketSortType,
} from './application/types'
import { AvailabilityNotification } from './components/AvailabilityNotification/AvailabilityNotification'
import { FormRequest } from './components/form/FormRequests'
import { PublicEmptyLegResults } from './components/PublicEmptyLegResults/PublicEmptyLegResults'
import { AppDispatch } from './rtk/store'
import {
    appliedSearchSelector,
    emptyLegsErrorSelector,
    emptyLegsStatusSelector,
    formDataSelector,
    getEmptyLegs,
    publicEmptyLegResultsSelector,
    resetSearch,
    setSortTab,
    sortTabSelector,
    updateField,
} from './rtk/tableSlice'

type PublicEmptyLegNotificationError = {
    error?: string
}

const getNotificationErrorMessage = (error: unknown) => (
    axios.isAxiosError<PublicEmptyLegNotificationError>(error)
        ? error.response?.data?.error
        : undefined
)

const Page = () => {
    const dispatch = useDispatch<AppDispatch>()
    const results = useSelector(publicEmptyLegResultsSelector)
    const selectedFilter = useSelector(sortTabSelector)
    const formData = useSelector(formDataSelector)
    const appliedSearch = useSelector(appliedSearchSelector)
    const status = useSelector(emptyLegsStatusSelector)
    const searchError = useSelector(emptyLegsErrorSelector)
    const isLoading = status === 'idle' || status === 'loading'

    useEffect(() => {
        dispatch(getEmptyLegs())
    }, [dispatch])

    useEffect(() => {
        if (searchError) {
            setNotification({ type: 'error', message: searchError })
        }
    }, [searchError])

    const handleSortTabChange = useCallback((value: PublicEmptyLegMarketSortType) => {
        sendGoogleGoal(GOOGLE_EVENTS.public_empty_legs_sort, { sort: value })
        dispatch(setSortTab(value))
        dispatch(getEmptyLegs())
    }, [dispatch])

    const onOpenModalRegistration = useCallback(() => {
        dispatch(openFormRegistration())
    }, [dispatch])

    const handleOnChange = useCallback((update: PublicEmptyLegFilterUpdate) => {
        dispatch(updateField(update))
    }, [dispatch])

    const handleOnSubmit = useCallback(() => {
        sendGoogleGoal(GOOGLE_EVENTS.public_empty_legs_search)
        dispatch(getEmptyLegs())
    }, [dispatch])

    const handleResetSearch = useCallback(() => {
        sendGoogleGoal(GOOGLE_EVENTS.public_empty_legs_reset)
        dispatch(resetSearch())
        dispatch(getEmptyLegs())
    }, [dispatch])

    const handleNotify = useCallback(async (email: string) => {
        try {
            await PublicEmptyLegsApi.notifyPublicEmptyLegs({
                email,
                from: appliedSearch.from_location?.iata_code || appliedSearch.from_location?.icao_code,
                destination: appliedSearch.destination_location?.iata_code || appliedSearch.destination_location?.icao_code,
                startDate: appliedSearch.dates?.startDate?.toISOString(),
                endDate: appliedSearch.dates?.endDate?.toISOString(),
            })
            setNotification({ type: 'success', message: 'Notification request sent.' })
            sendGoogleGoal(GOOGLE_EVENTS.public_empty_legs_notify)
        } catch (error: unknown) {
            setNotification({ type: 'error', message: getNotificationErrorMessage(error) || 'Unable to send your notification request' })
            throw error
        }
    }, [appliedSearch])

    return (
        <>
            <PageMeta pageKey={PageKey.PUBLIC_EMPTY_LEGS} />
            <div className="public-empty-legs grey-block">
                <SiteHeader onOpenRegistration={onOpenModalRegistration} />
                <div className="blue-block_padding">
                    <FormRequest
                        selected={formData}
                        isLoading={isLoading}
                        onChange={handleOnChange}
                        onSubmit={handleOnSubmit}
                    />
                </div>
                <main
                    className="public-empty-legs__content container"
                    onClickCapture={(event) => trackPublicCatalogLink(event, 'public_empty_legs')}
                >
                    <h1 className="public-empty-legs__title">Hot Cargo Capacity</h1>
                    <section className="public-empty-legs__introduction">
                        <h2>How Hot Capacity Works</h2>
                        <p>
                            Hot Capacity includes Empty Legs, Part Charters and Available From opportunities published by operators.
                            They can offer a practical charter option when the departure point, destination, date and available aircraft capacity
                            match your shipment. Search the published Hot Capacity below by route and date, then review the aircraft type and
                            payload before requesting details. Availability can change as operators update their schedules, and every movement
                            remains subject to operational approval, permits and final commercial terms. If no listed option matches your route,
                            submit a cargo charter request so verified operators can respond with suitable capacity. You can also compare
                            aircraft specifications in the Aircraft Wiki before discussing loading requirements with an operator.
                        </p>
                        <div className="public-empty-legs__introduction-links">
                            <button
                                type="button"
                                onClick={() => {
                                    sendGoogleGoal(GOOGLE_EVENTS.public_empty_legs_request_charter)
                                    onOpenModalRegistration()
                                }}
                            >
                                Submit a cargo charter request
                            </button>
                            <a href="/models">Compare cargo aircraft models</a>
                        </div>
                    </section>
                    <PublicEmptyLegResults
                        presentation={results}
                        selectedSort={selectedFilter}
                        onSortChange={handleSortTabChange}
                        onGetInfo={() => {
                            sendGoogleGoal(GOOGLE_EVENTS.public_empty_legs_get_info)
                            onOpenModalRegistration()
                        }}
                        onResetSearch={handleResetSearch}
                        isInitialSearch={!hasPublicEmptyLegSearchCriteria(appliedSearch)}
                        isLoading={isLoading}
                    />
                    <AvailabilityNotification onNotify={handleNotify} />
                </main>
                <SiteFooter />
            </div>
            <RegistrationModalWrapper />
        </>
    )
}
export default Page
